/**
 * FERP Portal - Audit Logger Hook
 *
 * Provides audit logging for all repository operations.
 * Life-critical systems require comprehensive audit trails.
 *
 * CONSTRAINTS:
 * - All user actions MUST be logged
 * - Immutable audit trails (append-only)
 * - Tenant-scoped logging
 */

import { useCallback, useRef } from 'react';
import { gql } from '@apollo/client';
import { useTenantId } from '@/auth';
import { apolloClient } from '@/utils/apollo-client';

// GraphQL mutation for batch audit logging
const CREATE_AUDIT_BATCH = gql`
  mutation CreateAuditBatch($input: CreateAuditBatchInput!) {
    createAuditBatch(input: $input) {
      successCount
      failureCount
      errors {
        index
        message
      }
    }
  }
`;

// Audit log entry
export interface AuditLogEntry {
  timestamp: string;
  tenantId: string;
  entityType: string;
  operation: AuditOperation;
  entityId?: string;
  variables?: Record<string, unknown>;
  userId?: string;
  sessionId?: string;
  duration?: number;
  success: boolean;
  error?: string;
}

// Audit operations
export type AuditOperation =
  | 'list'
  | 'get'
  | 'create'
  | 'update'
  | 'delete'
  | 'archive'
  | 'restore'
  | 'approve'
  | 'reject';

// Logger configuration
export interface AuditLoggerConfig {
  entityType: string;
  enabled?: boolean;
  logToConsole?: boolean;
  logToBackend?: boolean;
}

// Audit buffer for batched logging
const auditBuffer: AuditLogEntry[] = [];
const BUFFER_FLUSH_INTERVAL = 5000; // 5 seconds
const BUFFER_MAX_SIZE = 100;

// Flush audit buffer to backend
async function flushAuditBuffer(): Promise<void> {
  if (auditBuffer.length === 0) return;

  const entries = auditBuffer.splice(0, auditBuffer.length);

  try {
    const client = apolloClient;

    // Transform entries to backend format
    const auditEntries = entries.map(entry => ({
      timestamp: entry.timestamp,
      tenantId: entry.tenantId,
      entityType: entry.entityType,
      action: entry.operation.toUpperCase(),
      entityId: entry.entityId,
      metadata: entry.variables ? JSON.stringify(entry.variables) : null,
      userId: entry.userId,
      sessionId: entry.sessionId,
      durationMs: entry.duration,
      success: entry.success,
      errorMessage: entry.error,
    }));

    await client.mutate<Record<string, any>>({
      mutation: CREATE_AUDIT_BATCH,
      variables: {
        input: {
          entries: auditEntries,
        },
      },
    });

    console.debug(`📝 Flushed ${entries.length} audit entries to backend`);
  } catch (error) {
    // Re-add entries on failure (best effort)
    auditBuffer.unshift(...entries);
    console.error('Failed to flush audit buffer:', error);
  }
}

// Start periodic flush
if (typeof window !== 'undefined') {
  setInterval(flushAuditBuffer, BUFFER_FLUSH_INTERVAL);
}

/**
 * Audit logger hook for repository operations
 */
export function useAuditLogger(entityType: string, config?: Partial<AuditLoggerConfig>) {
  const tenantId = useTenantId();
  const configRef = useRef<AuditLoggerConfig>({
    entityType,
    enabled: true,
    logToConsole: process.env.NODE_ENV === 'development',
    logToBackend: true,
    ...config,
  });

  /**
   * Log an audit entry
   */
  const log = useCallback(
    (
      operation: AuditOperation,
      options: {
        entityId?: string;
        variables?: Record<string, unknown>;
        success?: boolean;
        error?: string;
        duration?: number;
      } = {}
    ): void => {
      if (!configRef.current.enabled) return;

      const entry: AuditLogEntry = {
        timestamp: new Date().toISOString(),
        tenantId: tenantId || 'unknown',
        entityType: configRef.current.entityType,
        operation,
        entityId: options.entityId,
        variables: options.variables,
        success: options.success ?? true,
        error: options.error,
        duration: options.duration,
      };

      // Log to console in development
      if (configRef.current.logToConsole) {
        const icon = entry.success ? '✅' : '❌';
        console.debug(
          `${icon} [Audit] ${entry.entityType}.${entry.operation}`,
          {
            entityId: entry.entityId,
            duration: entry.duration ? `${entry.duration}ms` : undefined,
            tenant: entry.tenantId,
          }
        );
      }

      // Add to buffer for backend logging
      if (configRef.current.logToBackend) {
        auditBuffer.push(entry);

        // Flush if buffer is full
        if (auditBuffer.length >= BUFFER_MAX_SIZE) {
          flushAuditBuffer();
        }
      }
    },
    [tenantId]
  );

  /**
   * Trace an operation (logs start and completion)
   */
  const trace = useCallback(
    (operation: AuditOperation, variables?: Record<string, unknown>) => {
      const startTime = Date.now();

      return {
        success: (entityId?: string) => {
          log(operation, {
            entityId,
            variables,
            success: true,
            duration: Date.now() - startTime,
          });
        },
        failure: (error: Error | string, entityId?: string) => {
          log(operation, {
            entityId,
            variables,
            success: false,
            error: typeof error === 'string' ? error : error.message,
            duration: Date.now() - startTime,
          });
        },
      };
    },
    [log]
  );

  /**
   * Create a wrapped async function with automatic audit logging
   */
  const withAudit = useCallback(
    <T extends (...args: any[]) => Promise<any>>(
      operation: AuditOperation,
      fn: T,
      getEntityId?: (result: Awaited<ReturnType<T>>) => string | undefined
    ): T => {
      return (async (...args: Parameters<T>) => {
        const startTime = Date.now();
        try {
          const result = await fn(...args);
          log(operation, {
            entityId: getEntityId?.(result),
            success: true,
            duration: Date.now() - startTime,
          });
          return result;
        } catch (error) {
          log(operation, {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
            duration: Date.now() - startTime,
          });
          throw error;
        }
      }) as T;
    },
    [log]
  );

  return {
    log,
    trace,
    withAudit,
  };
}

export default useAuditLogger;
